import {
  DrawerDescription,
  FormMultiSelector,
  DrawerContent,
  DrawerHeader,
  ComboboxItem,
  DrawerTitle,
  DrawerClose,
  Typography,
  FormInput,
  Loading,
  Drawer,
  Button,
  Label,
  Form,
} from '@ui/components';
import { FormAssignTable } from '@shared/components/assign-table';
import { VehicleTableDataDto } from '@interfaces/models';
import { Cancel, Delete } from '@ui/components/icons';
import { formatPhoneNumber } from '@helpers/utils';
import { HasPermission } from '@shared/components';
import { useHasPermission } from '@shared/hooks';
import { FC } from 'react';

import { useEditVehicle } from './use-edit-vehicle';

interface EditVehicleDrawerProps {
  vehicle: VehicleTableDataDto;
  onClose: ({ handleSuccess }: { handleSuccess: boolean }) => void;
  onRemove: () => void;
}

export const EditVehicleDrawer: FC<EditVehicleDrawerProps> = ({ vehicle, onClose, onRemove }) => {
  const {
    isUpdateVehiclePending,
    assignedEmployees,
    costCenterOptions,
    assignTable,
    isLoading,
    onSubmit,
    form,
    t,
  } = useEditVehicle({
    vehicleId: vehicle.id,
    onSuccess: () => {
      onClose({ handleSuccess: true });
    },
  });

  const hasEditPermission = useHasPermission('VEHICLES_EDIT_VEHICLE', 'WRITE');
  const registrationNumber = form.watch('registrationNumber');
  const registrationCountry = form.watch('registrationCountry');
  const vehicleCategory = form.watch('vehicleCategory');

  return (
    <Drawer onClose={() => onClose({ handleSuccess: false })} open={!!vehicle} handleOnly>
      <DrawerContent className="flex flex-col gap-0 overflow-hidden p-0" draggable="false">
        <DrawerHeader className="flex flex-row items-center justify-between p-4 border-b border-border-weak">
          <DrawerTitle asChild>
            <Typography variant="h200">{t('VEHICLES.EDIT_VEHICLE_DRAWER_HEADER')}</Typography>
          </DrawerTitle>
          <DrawerDescription />
          <div className="flex flex-row gap-2">
            <HasPermission permissionFeature="VEHICLES_EDIT_VEHICLE" permissionType="WRITE">
              <Button onClick={onRemove} variant="ghost" size="small" icon>
                <Delete />
              </Button>
            </HasPermission>
            <DrawerClose asChild>
              <Button variant="ghost" size="small" icon>
                <Cancel />
              </Button>
            </DrawerClose>
          </div>
        </DrawerHeader>
        <form className="flex-1 overflow-hidden flex flex-col gap-2" onSubmit={onSubmit}>
          <Loading isLoading={isLoading} className="flex-1">
            <div className="flex-1 overflow-y-auto p-4 pt-5">
              <div className="space-y-4">
                <Form {...form}>
                  <div className="grid grid-cols-6 gap-2 items-center border-b border-border-weak pb-4">
                    <Label htmlFor="registrationNumber" className="col-span-2">
                      <Typography variant="labelSmallBook" className="text-weak" element="label">
                        {t('VEHICLES.REGISTRATION_NUMBER')}
                      </Typography>
                    </Label>
                    <div className="col-span-4 h-10 flex items-center">
                      <Typography
                        className="text-paragraph col-span-4"
                        variant="labelMediumBook"
                        element="span"
                      >
                        {registrationNumber}
                      </Typography>
                    </div>

                    <Label className="col-span-2" htmlFor="vehicleName">
                      <Typography variant="labelSmallBook" className="text-weak" element="label">
                        {t('VEHICLES.VEHICLE_NAME')}
                      </Typography>
                    </Label>
                    <FormInput
                      componentProps={{
                        id: 'vehicleName',
                      }}
                      placeholder={t('VEHICLES.VEHICLE_NAME')}
                      disabled={!hasEditPermission}
                      className="w-full col-span-4"
                      formControl={form.control}
                      size="small"
                      name="name"
                      required
                    />

                    {costCenterOptions.length > 1 && (
                      <>
                        <Label className="col-span-2" htmlFor="costCenter">
                          <Typography
                            variant="labelSmallBook"
                            className="text-weak"
                            element="label"
                          >
                            {t('VEHICLES.COST_CENTER')}
                          </Typography>
                        </Label>
                        <FormMultiSelector
                          placeholder={t('VEHICLES.COST_CENTER')}
                          options={costCenterOptions ?? []}
                          disabled={!hasEditPermission}
                          formControl={form.control}
                          className="col-span-4"
                          name="costCenters"
                          size="small"
                        />
                      </>
                    )}

                    <Label htmlFor="registrationCountry" className="col-span-2">
                      <Typography variant="labelSmallBook" className="text-weak" element="label">
                        {t('VEHICLES.REGISTRATION_COUNTRY')}
                      </Typography>
                    </Label>
                    <div className="col-span-4 h-10 flex items-center">
                      <Typography
                        className="text-paragraph col-span-4"
                        variant="labelMediumBook"
                        element="span"
                      >
                        {registrationCountry}
                      </Typography>
                    </div>

                    <Label htmlFor="vehicleCategory" className="col-span-2">
                      <Typography variant="labelSmallBook" className="text-weak" element="label">
                        {t('VEHICLES.VEHICLE_CATEGORY')}
                      </Typography>
                    </Label>
                    <div className="col-span-4 h-10 flex items-center">
                      {!!vehicleCategory && (
                        <Typography
                          className="text-paragraph col-span-4"
                          variant="labelMediumBook"
                          element="span"
                        >
                          {t(`VEHICLE_CATEGORY.${vehicleCategory}`)}
                        </Typography>
                      )}
                    </div>
                  </div>

                  <FormAssignTable
                    itemTemplate={(item) => (
                      <ComboboxItem
                        keywords={[
                          item.firstName ?? '',
                          item.lastName ?? '',
                          item.phoneNumber ?? '',
                        ]}
                        disabled={assignedEmployees?.includes(item.id)}
                        className="justify-between h-12"
                        label={item.id}
                        value={item.id}
                        key={item.id}
                      >
                        <Typography variant="labelMediumBook">
                          {item.firstName ?? '-'} {item.lastName ?? '-'}
                        </Typography>
                        <Typography variant="labelMediumBook">
                          {formatPhoneNumber(item.phoneNumber, item.phoneExt)}
                        </Typography>
                      </ComboboxItem>
                    )}
                    text={{
                      removeButtonText: t('VEHICLES.EMPLOYEES_ASSIGN_REMOVE'),
                      assignButtonText: t('VEHICLES.EMPLOYEES_ASSIGN_ADD'),
                      comboboxPlaceholder: t('VEHICLES.EMPLOYEES_ASSIGN_COMBOBOX_PLACEHOLDER'),
                      comboboxNoResults: t('VEHICLES.EMPLOYEES_ASSIGN_NO_RESULT'),
                      tableNoResults: t('VEHICLES.EMPLOYEES_ASSIGN_NO_RESULT'),
                      searchPlaceholder: t('VEHICLES.TABLE_SEARCH_PLACEHOLDER'),
                      title: t('VEHICLES.EMPLOYEES_ASSIGN_TABLE_TITLE'),
                      selectedRows: (count) =>
                        t('VEHICLES.EMPLOYEES_ASSIGN_SELECTED_ROWS', {
                          postProcess: 'interval',
                          count,
                        }),
                    }}
                    disabled={!hasEditPermission}
                    formControl={form.control}
                    name="assignedEmployees"
                    {...assignTable}
                  />
                </Form>
              </div>
            </div>
          </Loading>
          <div className="p-4 shrink-0">
            <Button
              disabled={isLoading || isUpdateVehiclePending || !hasEditPermission}
              isLoading={isLoading || isUpdateVehiclePending}
              type="submit"
              size="large"
              wide
            >
              {t('VEHICLES.UPDATE_VEHICLE_SUBMIT')}
            </Button>
          </div>
        </form>
      </DrawerContent>
    </Drawer>
  );
};
